import { useToast } from '@chakra-ui/react';
import { useCallback } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { Util, FinancialCovenant } from '@finley-technologies/finley-types';

import { FinancialCovenantInputWorkleaseGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  useProduceFinancialCovenantWorkleaseMessageMutation,
  useEvaluateDevFinCovWorkleaseMutation,
} from '../requests/financialCovenants.gql.generated';
import {
  errorToastBaseConfig,
  successToastBaseConfig,
} from 'packages/common-frontend/constants';
import { CreditAgreementFullArtifactWipConfigType } from '~/packages/creditAgreements/ui/types';

type FinCovCreditAgreementEvalFunctionParams = {
  wipConfig: CreditAgreementFullArtifactWipConfigType;
  triggerDate: Util.FullDateStr;
  covenantKey?: string;
};

type FinCovApiFunction = (params: {
  variables: { worklease: FinancialCovenantInputWorkleaseGQLType };
}) => Promise<any>;

export const useSendFinancialCovenantWorklease = (): {
  isSendFinCovWorkleaseLoading: boolean;
  isEvaluateDevFinCovWorkleaseLoading: boolean;
  sendCreditAgreementFinancialCovenantWorklease: (
    inputs: FinCovCreditAgreementEvalFunctionParams,
  ) => Promise<void>;
  evaluateCreditAgreementDevFinCovWorklease: (
    inputs: FinCovCreditAgreementEvalFunctionParams,
  ) => Promise<void>;
  sendFinancialCovenantWorklease: (
    inputs: FinancialCovenantInputWorkleaseGQLType,
  ) => Promise<void>;
  evaluateDevFinCovWorklease: (
    inputs: FinancialCovenantInputWorkleaseGQLType,
  ) => Promise<void>;
} => {
  const toast = useToast();

  // *** Used API Mutations ***
  const [
    produceFinCovMessageApi,
    { loading: isSendFinCovWorkleaseLoading },
  ] = useProduceFinancialCovenantWorkleaseMessageMutation({
    onCompleted: () => {
      toast({
        ...successToastBaseConfig,
        title: 'Initiated Financial Covenants evaluation based on latest CA.',
      });
    },
    onError: (error) => {
      const title = commonUtils.EnvUtils.isProduction()
        ? 'Unable to initiate Financial Covenants evaluation. Please contact technical assistance if this continues.'
        : `Failed to initiate Financial Covenants evaluation: ${error.message}`;
      toast({
        ...errorToastBaseConfig,
        title,
      });
    },
  });

  const [
    evaluateDevFinCov,
    { loading: isEvaluateDevFinCovWorkleaseLoading },
  ] = useEvaluateDevFinCovWorkleaseMutation({
    onCompleted: () => {
      toast({
        ...successToastBaseConfig,
        title: 'Evaluated Financial Covenants based on latest CA.',
      });
    },
    onError: (error) => {
      const title = commonUtils.EnvUtils.isProduction()
        ? 'Unable to evaluate Financial Covenants worklease. Please contact technical assistance if this continues.'
        : `Failed to evaluate Financial Covenants worklease: ${error.message}`;
      toast({
        ...errorToastBaseConfig,
        title,
      });
    },
  });

  // *** Core Generic Wrapper Function ***
  const _setupFinCovEvalFunction = useCallback(
    async (
      inputs: {
        creditAgreementId: string;
        creditFacilityId: string;
        triggerDate: Util.FullDateStr;
        covenantKeys: string[];
        mode: FinancialCovenant.FinancialCovenantWorkleaseMode;
      },
      apiFunction: FinCovApiFunction,
    ): Promise<void> => {
      if (inputs.covenantKeys.length > 0) {
        await apiFunction({ variables: { worklease: inputs } });
      } else {
        toast({
          title: 'No Financial Covenant configs exist to evaluate',
          status: 'warning',
          position: 'top',
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [toast],
  );

  // *** Credit Agreement Wrapper Version ***
  const _setupFinCovCreditAgreementEvalFunction = useCallback(
    async (
      inputs: FinCovCreditAgreementEvalFunctionParams,
      apiFunction: FinCovApiFunction,
    ): Promise<void> => {
      const { wipConfig, triggerDate, covenantKey } = inputs;

      const creditAgreementId = wipConfig.formProps.values.id;
      const creditFacilityId = wipConfig.formProps.values.creditFacilityId;

      const covenantKeys: string[] = [];
      if (covenantKey) {
        covenantKeys.push(covenantKey);
      } else {
        for (const finCov of wipConfig.formProps.values.financialCovenants) {
          if (finCov._config_) {
            covenantKeys.push(finCov.covenantKey);
          }
        }
      }

      await _setupFinCovEvalFunction(
        {
          creditAgreementId,
          creditFacilityId,
          triggerDate,
          covenantKeys,
          mode: 'QA',
        },
        apiFunction,
      );
    },
    [_setupFinCovEvalFunction],
  );

  // *** Exposed Credit Agreement Functions ***
  const sendCreditAgreementFinancialCovenantWorklease = useCallback(
    async (inputs: FinCovCreditAgreementEvalFunctionParams): Promise<void> => {
      await _setupFinCovCreditAgreementEvalFunction(
        inputs,
        produceFinCovMessageApi,
      );
    },
    [_setupFinCovCreditAgreementEvalFunction, produceFinCovMessageApi],
  );

  const evaluateCreditAgreementDevFinCovWorklease = useCallback(
    async (inputs: FinCovCreditAgreementEvalFunctionParams): Promise<void> => {
      await _setupFinCovCreditAgreementEvalFunction(inputs, evaluateDevFinCov);
    },
    [_setupFinCovCreditAgreementEvalFunction, evaluateDevFinCov],
  );

  // *** Exposed Base Functions ***
  const sendFinancialCovenantWorklease = useCallback(
    async (inputs: FinancialCovenantInputWorkleaseGQLType): Promise<void> => {
      await _setupFinCovEvalFunction(inputs, produceFinCovMessageApi);
    },
    [_setupFinCovEvalFunction, produceFinCovMessageApi],
  );

  const evaluateDevFinCovWorklease = useCallback(
    async (inputs: FinancialCovenantInputWorkleaseGQLType): Promise<void> => {
      await _setupFinCovEvalFunction(inputs, evaluateDevFinCov);
    },
    [_setupFinCovEvalFunction, evaluateDevFinCov],
  );

  return {
    isSendFinCovWorkleaseLoading,
    isEvaluateDevFinCovWorkleaseLoading,
    sendCreditAgreementFinancialCovenantWorklease,
    evaluateCreditAgreementDevFinCovWorklease,
    sendFinancialCovenantWorklease,
    evaluateDevFinCovWorklease,
  };
};
